import React, { useMemo, useState } from "react";
import {
    Users,
    Clock,
    Plane,
    Receipt,
    Bell,
    CheckCircle,
    RefreshCw,
} from "lucide-react";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import "../DashboardStyles/DashboardManager.css";

const teamData = {
    teamName: "Équipe Développement Frontend",
    manager: "Sophie Martin",
    totalMembers: 12,
    pendingLeaves: 4,
    ongoingMissions: 2,
    pendingExpenses: 6,
    notifications: [
        { id: 1, text: "Demande de congé - Jean Dupont (3 jours)", type: "leave" },
        { id: 2, text: "Note de frais en attente - Marie Lambert (850 MAD)", type: "expense" },
        { id: 3, text: "Mission terminée - Paul Durand", type: "mission" },
    ],
};

const StatCard = ({ label, value, icon, color, note }) => (
    <div className="dmStatCard">
        <div className="dmStatLeft">
            <div className="dmStatLabel">{label}</div>
            <div className="dmStatValue" style={{ color }}>{value}</div>
            {note && <div className="dmStatNote">{note}</div>}
        </div>
        <div className="dmStatIcon" style={{ color }}>{icon}</div>
    </div>
);

const DashboardManager = () => {
    const [loading, setLoading] = useState(false);

    const refresh = () => {
        setLoading(true);
        setTimeout(() => setLoading(false), 700);
    };

    // ✅ Exemple de données charts
    const pieData = useMemo(
        () => [
            { name: "Congés", value: 6 },
            { name: "Absences", value: 3 },
            { name: "RTT", value: 2 },
            { name: "Autres", value: 1 },
        ],
        []
    );

    const barData = useMemo(
        () => [
            { name: "Transport", value: 4200 },
            { name: "Hôtel", value: 3100 },
            { name: "Repas", value: 1600 },
            { name: "Autres", value: 900 },
        ],
        []
    );

    // couleurs (tu peux les changer)
    const PIE_COLORS = ["#204385", "#ee645f", "#10b981", "#f59e0b"];

    return (
        <div className="dmPage">
            <div className="dmWrap">
                {/* Header */}
                <div className="dmHeader">
                    <div>
                        <h1>Tableau de bord</h1>

                    </div>

                    {/*<button className="dmRefreshBtn" onClick={refresh} disabled={loading}>*/}
                    {/*    <RefreshCw size={16} className={loading ? "spin" : ""} />*/}
                    {/*    Actualiser*/}
                    {/*</button>*/}
                </div>

                {/* KPI */}
                <div className="dmStats">
                    <StatCard
                        label="Membres d'équipe"
                        value={teamData.totalMembers}
                        icon={<Users size={22} />}
                        color="#204385"
                    />
                    <StatCard
                        label="Congés en attente"
                        value={teamData.pendingLeaves}
                        icon={<Clock size={22} />}
                        color="#f59e0b"
                        note="À valider"
                    />
                    <StatCard
                        label="Missions en cours"
                        value={teamData.ongoingMissions}
                        icon={<Plane size={22} />}
                        color="#2196f3"
                    />
                    <StatCard
                        label="Notes de frais"
                        value={teamData.pendingExpenses}
                        icon={<Receipt size={22} />}
                        color="#ee645f"
                        note="En attente"
                    />
                </div>

                {/* Charts */}
                <div className="dmGrid">
                    {/* Pie */}
                    <div className="dmCard">
                        <h3>Répartition des absences</h3>
                        <div className="dmChartBox">
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={55}
                                        outerRadius={85}
                                        paddingAngle={3}
                                    >
                                        {pieData.map((_, idx) => (
                                            <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>

                            <div className="dmLegend">
                                {pieData.map((d, idx) => (
                                    <div key={d.name} className="dmLegendItem">
                    <span
                        className="dmLegendDot"
                        style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }}
                    />
                                        <span className="dmLegendName">{d.name}</span>
                                        <span className="dmLegendVal">{d.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Bar */}
                    <div className="dmCard">
                        <h3>Dépenses par catégorie (MAD)</h3>
                        <div className="dmChartBox">
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={barData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" />
                                    <YAxis />
                                    <Tooltip />
                                    <Bar dataKey="value" fill="#204385" radius={[6, 6, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                {/* Notifications */}
                <div className="dmSectionTitle">
                    <Bell size={18} />
                    Alertes & Notifications
                </div>

                <div className="dmCard dmNoPad">
                    {teamData.notifications.map((n) => (
                        <div key={n.id} className="dmNotifRow">
                            <div className={`dmNotifIcon dmNotifIcon--${n.type}`}>
                                {n.type === "leave" && <Clock size={16} />}
                                {n.type === "expense" && <Receipt size={16} />}
                                {n.type === "mission" && <CheckCircle size={16} />}
                            </div>
                            <div className="dmNotifText">
                                <div>{n.text}</div>
                                <div className="dmSub">{new Date().toLocaleDateString("fr-FR")}</div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="dmFooter">
                    Dernière mise à jour : {new Date().toLocaleString("fr-FR")}
                </div>
            </div>
        </div>
    );
};

export default DashboardManager;
